import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LuArrowLeft, LuLayoutDashboard, LuTable, LuChartColumn, LuChartPie,
    LuChartLine, LuHash, LuPlus,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RelatorioPersonalizadoService, {
    type RelatorioSalvo, type VisualRelatorio,
} from '../services/RelatorioPersonalizadoService';
import './Relatorios.css';

/* A biblioteca de modelos — a primeira porta de "Novo relatório" desde 18/09.
   Antes daqui, o botão abria o construtor em branco: fonte, medida, dimensão,
   visual. Ele usou e disse o que se esperava de quem não montou o catálogo:
   "um número que não me diz nada". Ninguém encara uma folha em branco.

   O que esta tela mostra NÃO é uma lista de configurações: é a lista de
   perguntas que a plataforma já sabe responder. E não há engrenagem nova por
   trás — cada modelo é um relatório pronto do servidor (id negativo), com o
   painel inteiro. Escolher um leva o painel para o editor SEM salvar nada; o
   relatório só nasce quando a pessoa clica em Salvar. Quem desistir no meio
   não deixa lixo na galeria.

   A folha em branco continua existindo, no fim e tracejada: é a segunda porta,
   não a primeira.                                                            */

const ICONE: Record<VisualRelatorio, React.ReactNode> = {
    numero: <LuHash size={12} />,
    tabela: <LuTable size={12} />,
    barras: <LuChartColumn size={12} />,
    linha: <LuChartLine size={12} />,
    pizza: <LuChartPie size={12} />,
    lista: <LuTable size={12} />,
};

const ROTULO: Record<VisualRelatorio, string> = {
    numero: 'Número',
    tabela: 'Tabela',
    barras: 'Barras',
    linha: 'Linha',
    pizza: 'Pizza',
    lista: 'Lista',
};

function Modelo({ r, onEscolher }: { r: RelatorioSalvo; onEscolher: () => void }) {
    const painel = r.totalBlocos > 1;
    return (
        <button type="button" className="rl-mod" onClick={onEscolher}>
            <span className="rl-mod-q">{r.nome}</span>
            <span className="rl-mod-d">{r.descricao}</span>
            <span className="rl-mod-f">
                {painel ? <LuLayoutDashboard size={12} /> : ICONE[r.visual] ?? <LuTable size={12} />}
                <span>{painel ? `${r.totalBlocos} blocos` : ROTULO[r.visual] ?? 'Tabela'}</span>
            </span>
        </button>
    );
}

export default function RelatorioModelosPage() {
    const navegar = useNavigate();

    const [modelos, setModelos] = useState<RelatorioSalvo[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        let montado = true;
        (async () => {
            try {
                const todos = await RelatorioPersonalizadoService.listar();
                if (montado) setModelos(todos.filter(r => r.sistema));
            } catch (e) {
                if (montado) setErro(e instanceof Error ? e.message : 'Não foi possível carregar os modelos.');
            } finally {
                if (montado) setCarregando(false);
            }
        })();
        return () => { montado = false; };
    }, []);

    /* Painel e pergunta se separam pelo número de blocos, e não por uma etiqueta
       no servidor: um modelo de painel É um relatório com vários blocos. */
    const paineis = useMemo(() => modelos.filter(m => m.totalBlocos > 1), [modelos]);
    const perguntas = useMemo(() => modelos.filter(m => m.totalBlocos <= 1), [modelos]);

    const voltar = (
        <button type="button" className="rl-back" aria-label="Voltar para Relatórios"
            onClick={() => navegar('/relatorios')}>
            <LuArrowLeft size={16} />
        </button>
    );

    return (
        <div className="page-shell rl-screen">
            <PageHeader
                leading={voltar}
                title="O que você quer acompanhar?"
                subtitle="Escolha um ponto de partida. Você acrescenta, tira e reorganiza blocos depois."
            />

            <div className="page-body">
                {erro && (
                    <div className="rl-vazio">
                        <p className="rl-vazio-t">Não foi possível carregar os modelos</p>
                        <p className="rl-vazio-s">{erro}</p>
                        <button type="button" className="rl-btn" onClick={() => navegar('/relatorios/novo/branco')}>
                            Começar com um painel em branco
                        </button>
                    </div>
                )}

                {carregando && (
                    <div className="rl-mods">
                        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="rl-mod is-skel" />)}
                    </div>
                )}

                {!carregando && !erro && (
                    <>
                        {paineis.length > 0 && (
                            <>
                                <p className="rl-sec">Painéis prontos</p>
                                <div className="rl-mods">
                                    {paineis.map(m => (
                                        <Modelo key={m.relatorioSalvoId} r={m}
                                            onEscolher={() => navegar(`/relatorios/novo/${m.relatorioSalvoId}`)} />
                                    ))}
                                </div>
                            </>
                        )}

                        <p className="rl-sec is-mt">Perguntas de um bloco</p>
                        <div className="rl-mods">
                            {perguntas.map(m => (
                                <Modelo key={m.relatorioSalvoId} r={m}
                                    onEscolher={() => navegar(`/relatorios/novo/${m.relatorioSalvoId}`)} />
                            ))}

                            <button type="button" className="rl-mod is-branco"
                                onClick={() => navegar('/relatorios/novo/branco')}>
                                <span className="rl-mod-q"><LuPlus size={15} /> Painel em branco</span>
                                <span className="rl-mod-d">Começar sem nada e montar bloco a bloco.</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
